import { supabase } from './supabase';
import { encryptData, decryptData } from './encryption';
import {
  PasswordVault,
  PasswordItem,
  PasswordItemType,
  CreateVaultForm,
  CreatePasswordItemForm,
  VaultWithItemCount,
  SharedVaultWithDetails,
  PasswordItemWithDetails,
  ShareVaultForm,
  ShareItemForm,
  PasswordItemFilters,
  VaultFilters,
  ExportOptions,
  ImportResult,
  PermissionLevel,
  ActivityAction,
  VaultActivityAction,
  PasswordBreach,
  SecurityAuditLog,
  PasswordAlert,
  AlertType,
  AlertSeverity
} from '../types/password-management';

// ===== VAULT MANAGEMENT =====

export const passwordManagementService = {
  // Get user's vaults
  async getUserVaults(): Promise<VaultWithItemCount[]> {
    const { data, error } = await supabase
      .rpc('get_user_vaults', { user_uuid: (await supabase.auth.getUser()).data.user?.id });

    if (error) throw error;
    return data || [];
  },

  // Get shared vaults
  async getSharedVaults(): Promise<SharedVaultWithDetails[]> {
    const { data, error } = await supabase
      .rpc('get_shared_vaults', { user_uuid: (await supabase.auth.getUser()).data.user?.id });

    if (error) throw error;
    return data || [];
  },

  // Create new vault
  async createVault(vaultData: CreateVaultForm): Promise<PasswordVault> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('password_vaults')
      .insert({
        user_id: user.id,
        ...vaultData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update vault
  async updateVault(vaultId: string, updates: Partial<CreateVaultForm>): Promise<PasswordVault> {
    const { data, error } = await supabase
      .from('password_vaults')
      .update(updates)
      .eq('id', vaultId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete vault
  async deleteVault(vaultId: string): Promise<void> {
    const { error } = await supabase
      .from('password_vaults')
      .delete()
      .eq('id', vaultId);

    if (error) throw error;
  },

  // ===== PASSWORD ITEMS =====

  // Get items in a vault
  async getVaultItems(vaultId: string, filters?: PasswordItemFilters): Promise<PasswordItemWithDetails[]> {
    let query = supabase
      .from('password_items')
      .select(`
        *,
        vault:password_vaults(*),
        login_details:login_items(*),
        credit_card_details:credit_card_items(*),
        identity_details:identity_items(*),
        secure_note_details:secure_note_items(*),
        software_license_details:software_license_items(*),
        database_details:database_items(*),
        server_details:server_items(*),
        email_account_details:email_account_items(*),
        bank_account_details:bank_account_items(*),
        crypto_wallet_details:crypto_wallet_items(*),
        api_key_details:api_key_items(*),
        ssh_key_details:ssh_key_items(*),
        custom_details:custom_items(*)
      `)
      .eq('vault_id', vaultId);

    if (filters) {
      if (filters.item_type) query = query.eq('item_type', filters.item_type);
      if (filters.category) query = query.eq('category', filters.category);
      if (filters.favorite !== undefined) query = query.eq('favorite', filters.favorite);
      if (filters.strength) query = query.eq('strength', filters.strength);
      if (filters.search) query = query.ilike('name', `%${filters.search}%`);
      if (filters.tags && filters.tags.length > 0) query = query.overlaps('tags', filters.tags);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Create password item
  async createPasswordItem(itemData: CreatePasswordItemForm): Promise<PasswordItemWithDetails> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    // Start a transaction
    const { data: item, error: itemError } = await supabase
      .from('password_items')
      .insert({
        vault_id: itemData.vault_id,
        user_id: user.id,
        item_type: itemData.item_type,
        name: itemData.name,
        username: itemData.username,
        encrypted_password: itemData.password ? await encryptData(itemData.password) : undefined,
        url: itemData.url,
        notes: itemData.notes,
        category: itemData.category,
        tags: itemData.tags
      })
      .select()
      .single();

    if (itemError) throw itemError;

    // Create item-specific details
    if (itemData.login_details) {
      await this.createLoginDetails(item.id, itemData.login_details);
    }
    if (itemData.credit_card_details) {
      await this.createCreditCardDetails(item.id, itemData.credit_card_details);
    }
    if (itemData.identity_details) {
      await this.createIdentityDetails(item.id, itemData.identity_details);
    }
    if (itemData.secure_note_details) {
      await this.createSecureNoteDetails(item.id, itemData.secure_note_details);
    }
    if (itemData.software_license_details) {
      await this.createSoftwareLicenseDetails(item.id, itemData.software_license_details);
    }
    if (itemData.database_details) {
      await this.createDatabaseDetails(item.id, itemData.database_details);
    }
    if (itemData.server_details) {
      await this.createServerDetails(item.id, itemData.server_details);
    }
    if (itemData.email_account_details) {
      await this.createEmailAccountDetails(item.id, itemData.email_account_details);
    }
    if (itemData.bank_account_details) {
      await this.createBankAccountDetails(item.id, itemData.bank_account_details);
    }
    if (itemData.crypto_wallet_details) {
      await this.createCryptoWalletDetails(item.id, itemData.crypto_wallet_details);
    }
    if (itemData.api_key_details) {
      await this.createApiKeyDetails(item.id, itemData.api_key_details);
    }
    if (itemData.ssh_key_details) {
      await this.createSshKeyDetails(item.id, itemData.ssh_key_details);
    }
    if (itemData.custom_details) {
      await this.createCustomDetails(item.id, itemData.custom_details);
    }

    // Log activity
    await this.logItemActivity(item.id, 'created');

    return this.getPasswordItem(item.id);
  },

  // Get single password item with details
  async getPasswordItem(itemId: string): Promise<PasswordItemWithDetails> {
    const { data, error } = await supabase
      .from('password_items')
      .select(`
        *,
        vault:password_vaults(*),
        login_details:login_items(*),
        credit_card_details:credit_card_items(*),
        identity_details:identity_items(*),
        secure_note_details:secure_note_items(*),
        software_license_details:software_license_items(*),
        database_details:database_items(*),
        server_details:server_items(*),
        email_account_details:email_account_items(*),
        bank_account_details:bank_account_items(*),
        crypto_wallet_details:crypto_wallet_items(*),
        api_key_details:api_key_items(*),
        ssh_key_details:ssh_key_items(*),
        custom_details:custom_items(*)
      `)
      .eq('id', itemId)
      .single();

    if (error) throw error;
    return data;
  },

  // Update password item
  async updatePasswordItem(itemId: string, updates: Partial<CreatePasswordItemForm>): Promise<PasswordItemWithDetails> {
    const updateData: any = {
      name: updates.name,
      username: updates.username,
      url: updates.url,
      notes: updates.notes,
      category: updates.category,
      tags: updates.tags
    };

    if (updates.password) {
      updateData.encrypted_password = await encryptData(updates.password);
    }

    const { data, error } = await supabase
      .from('password_items')
      .update(updateData)
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;

    // Update item-specific details if provided
    if (updates.login_details) {
      await this.updateLoginDetails(itemId, updates.login_details);
    }
    if (updates.credit_card_details) {
      await this.updateCreditCardDetails(itemId, updates.credit_card_details);
    }
    // ... handle other item types

    // Log activity
    await this.logItemActivity(itemId, 'edited');

    return this.getPasswordItem(itemId);
  },

  // Delete password item
  async deletePasswordItem(itemId: string): Promise<void> {
    const { error } = await supabase
      .from('password_items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;

    // Log activity
    await this.logItemActivity(itemId, 'deleted');
  },

  // ===== ITEM-SPECIFIC DETAILS =====

  async createLoginDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('login_items')
      .insert({
        id: itemId,
        ...details,
        totp_secret_encrypted: details.totp_secret ? await encryptData(details.totp_secret) : undefined,
        backup_codes_encrypted: details.backup_codes ? await Promise.all(details.backup_codes.map((code: string) => encryptData(code))) : undefined
      });

    if (error) throw error;
  },

  async createCreditCardDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('credit_card_items')
      .insert({
        id: itemId,
        ...details,
        card_number_encrypted: details.card_number ? await encryptData(details.card_number) : undefined,
        cvv_encrypted: details.cvv ? await encryptData(details.cvv) : undefined,
        pin_encrypted: details.pin ? await encryptData(details.pin) : undefined
      });

    if (error) throw error;
  },

  async createIdentityDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('identity_items')
      .insert({
        id: itemId,
        ...details,
        social_security_number_encrypted: details.social_security_number ? await encryptData(details.social_security_number) : undefined,
        passport_number_encrypted: details.passport_number ? await encryptData(details.passport_number) : undefined,
        driver_license_number_encrypted: details.driver_license_number ? await encryptData(details.driver_license_number) : undefined
      });

    if (error) throw error;
  },

  async createSecureNoteDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('secure_note_items')
      .insert({
        id: itemId,
        ...details,
        encrypted_content: await encryptData(details.content)
      });

    if (error) throw error;
  },

  async createSoftwareLicenseDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('software_license_items')
      .insert({
        id: itemId,
        ...details,
        license_key_encrypted: details.license_key ? await encryptData(details.license_key) : undefined
      });

    if (error) throw error;
  },

  async createDatabaseDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('database_items')
      .insert({
        id: itemId,
        ...details,
        connection_string_encrypted: details.connection_string ? await encryptData(details.connection_string) : undefined
      });

    if (error) throw error;
  },

  async createServerDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('server_items')
      .insert({
        id: itemId,
        ...details,
        sudo_password_encrypted: details.sudo_password ? await encryptData(details.sudo_password) : undefined
      });

    if (error) throw error;
  },

  async createEmailAccountDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('email_account_items')
      .insert({
        id: itemId,
        ...details,
        smtp_password_encrypted: details.smtp_password ? await encryptData(details.smtp_password) : undefined,
        app_password_encrypted: details.app_password ? await encryptData(details.app_password) : undefined
      });

    if (error) throw error;
  },

  async createBankAccountDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('bank_account_items')
      .insert({
        id: itemId,
        ...details,
        account_number_encrypted: details.account_number ? await encryptData(details.account_number) : undefined,
        routing_number_encrypted: details.routing_number ? await encryptData(details.routing_number) : undefined
      });

    if (error) throw error;
  },

  async createCryptoWalletDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('crypto_wallet_items')
      .insert({
        id: itemId,
        ...details,
        wallet_address_encrypted: details.wallet_address ? await encryptData(details.wallet_address) : undefined,
        private_key_encrypted: details.private_key ? await encryptData(details.private_key) : undefined,
        seed_phrase_encrypted: details.seed_phrase ? await encryptData(details.seed_phrase) : undefined,
        api_key_encrypted: details.api_key ? await encryptData(details.api_key) : undefined,
        api_secret_encrypted: details.api_secret ? await encryptData(details.api_secret) : undefined,
        passphrase_encrypted: details.passphrase ? await encryptData(details.passphrase) : undefined
      });

    if (error) throw error;
  },

  async createApiKeyDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('api_key_items')
      .insert({
        id: itemId,
        ...details,
        api_key_encrypted: details.api_key ? await encryptData(details.api_key) : undefined,
        api_secret_encrypted: details.api_secret ? await encryptData(details.api_secret) : undefined,
        access_token_encrypted: details.access_token ? await encryptData(details.access_token) : undefined,
        refresh_token_encrypted: details.refresh_token ? await encryptData(details.refresh_token) : undefined
      });

    if (error) throw error;
  },

  async createSshKeyDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('ssh_key_items')
      .insert({
        id: itemId,
        ...details,
        private_key_encrypted: details.private_key ? await encryptData(details.private_key) : undefined,
        passphrase_encrypted: details.passphrase ? await encryptData(details.passphrase) : undefined
      });

    if (error) throw error;
  },

  async createCustomDetails(itemId: string, details: any) {
    const { error } = await supabase
      .from('custom_items')
      .insert({
        id: itemId,
        custom_fields: details.custom_fields,
        template_name: details.template_name
      });

    if (error) throw error;
  },

  // Update methods for item-specific details
  async updateLoginDetails(itemId: string, details: any) {
    const updateData: any = { ...details };
    if (details.totp_secret) {
      updateData.totp_secret_encrypted = await encryptData(details.totp_secret);
    }
    if (details.backup_codes) {
      updateData.backup_codes_encrypted = await Promise.all(details.backup_codes.map((code: string) => encryptData(code)));
    }

    const { error } = await supabase
      .from('login_items')
      .update(updateData)
      .eq('id', itemId);

    if (error) throw error;
  },

  async updateCreditCardDetails(itemId: string, details: any) {
    const updateData: any = { ...details };
    if (details.card_number) {
      updateData.card_number_encrypted = await encryptData(details.card_number);
    }
    if (details.cvv) {
      updateData.cvv_encrypted = await encryptData(details.cvv);
    }
    if (details.pin) {
      updateData.pin_encrypted = await encryptData(details.pin);
    }

    const { error } = await supabase
      .from('credit_card_items')
      .update(updateData)
      .eq('id', itemId);

    if (error) throw error;
  },

  // ===== SHARING =====

  // Share vault
  async shareVault(shareData: ShareVaultForm): Promise<void> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    // Check if user exists
    const { data: targetUser } = await supabase
      .from('auth.users')
      .select('id')
      .eq('email', shareData.email)
      .single();

    if (targetUser) {
      // Direct sharing
      const { error } = await supabase
        .from('vault_sharing')
        .insert({
          vault_id: shareData.vault_id,
          shared_by_user_id: user.id,
          shared_with_user_id: targetUser.id,
          permission_level: shareData.permission_level
        });

      if (error) throw error;
    } else {
      // Send invitation
      const { error } = await supabase
        .from('vault_invitations')
        .insert({
          vault_id: shareData.vault_id,
          invited_email: shareData.email,
          invited_name: shareData.name,
          permission_level: shareData.permission_level,
          invitation_token: crypto.randomUUID(),
          invitation_message: shareData.message,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
        });

      if (error) throw error;
    }
  },

  // Share item
  async shareItem(shareData: ShareItemForm): Promise<void> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    // Check if user exists
    const { data: targetUser } = await supabase
      .from('auth.users')
      .select('id')
      .eq('email', shareData.email)
      .single();

    if (targetUser) {
      // Direct sharing
      const { error } = await supabase
        .from('item_sharing')
        .insert({
          item_id: shareData.item_id,
          shared_by_user_id: user.id,
          shared_with_user_id: targetUser.id,
          permission_level: shareData.permission_level,
          include_password: shareData.include_password,
          include_notes: shareData.include_notes,
          include_attachments: shareData.include_attachments
        });

      if (error) throw error;
    } else {
      // Send invitation
      const { error } = await supabase
        .from('item_invitations')
        .insert({
          item_id: shareData.item_id,
          invited_email: shareData.email,
          invited_name: shareData.name,
          permission_level: shareData.permission_level,
          invitation_token: crypto.randomUUID(),
          invitation_message: shareData.message,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
        });

      if (error) throw error;
    }
  },

  // ===== ACTIVITY LOGGING =====

  async logItemActivity(itemId: string, action: ActivityAction, details?: any): Promise<void> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) return;

    const { error } = await supabase
      .from('password_item_activity')
      .insert({
        item_id: itemId,
        user_id: user.id,
        action,
        action_details: details,
        ip_address: '127.0.0.1', // In production, get from request
        user_agent: navigator.userAgent
      });

    if (error) console.error('Failed to log activity:', error);
  },

  async logVaultActivity(vaultId: string, action: VaultActivityAction, details?: any): Promise<void> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) return;

    const { error } = await supabase
      .from('vault_activity')
      .insert({
        vault_id: vaultId,
        user_id: user.id,
        action,
        action_details: details,
        ip_address: '127.0.0.1', // In production, get from request
        user_agent: navigator.userAgent
      });

    if (error) console.error('Failed to log vault activity:', error);
  },

  // ===== SECURITY =====

  async logSecurityAudit(action: string, resourceType?: string, resourceId?: string, details?: any, success = true, errorMessage?: string): Promise<void> {
    const user = (await supabase.auth.getUser()).data.user;

    const { error } = await supabase
      .from('security_audit_log')
      .insert({
        user_id: user?.id,
        action,
        resource_type: resourceType,
        resource_id: resourceId,
        action_details: details,
        ip_address: '127.0.0.1', // In production, get from request
        user_agent: navigator.userAgent,
        success,
        error_message: errorMessage
      });

    if (error) console.error('Failed to log security audit:', error);
  },

  async createPasswordBreach(itemId: string, breachData: Partial<PasswordBreach>): Promise<PasswordBreach> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('password_breaches')
      .insert({
        user_id: user.id,
        item_id: itemId,
        ...breachData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // ===== ALERTS =====

  async createAlert(alertData: Omit<PasswordAlert, 'id' | 'user_id' | 'created_at'>): Promise<PasswordAlert> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('password_alerts')
      .insert({
        user_id: user.id,
        ...alertData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getAlerts(): Promise<PasswordAlert[]> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('password_alerts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async markAlertAsRead(alertId: string): Promise<void> {
    const { error } = await supabase
      .from('password_alerts')
      .update({ is_read: true })
      .eq('id', alertId);

    if (error) throw error;
  },

  // ===== EXPORT/IMPORT =====

  async exportVault(vaultId: string, options: ExportOptions): Promise<string> {
    const items = await this.getVaultItems(vaultId);
    
    const exportData = {
      vault_id: vaultId,
      export_date: new Date().toISOString(),
      items: items.map(item => ({
        ...item,
        password: options.include_passwords ? item.encrypted_password : undefined,
        notes: options.include_notes ? item.notes : undefined
      }))
    };

    if (options.format === 'json') {
      return JSON.stringify(exportData, null, 2);
    } else if (options.format === 'csv') {
      return this.convertToCSV(exportData.items);
    } else {
      throw new Error('Unsupported export format');
    }
  },

  convertToCSV(items: any[]): string {
    if (items.length === 0) return '';

    const headers = ['name', 'username', 'password', 'url', 'notes', 'category', 'item_type'];
    const csvRows = [headers.join(',')];

    for (const item of items) {
      const row = headers.map(header => {
        const value = item[header] || '';
        return `"${value.toString().replace(/"/g, '""')}"`;
      });
      csvRows.push(row.join(','));
    }

    return csvRows.join('\n');
  },

  // ===== UTILITY FUNCTIONS =====

  async decryptPassword(encryptedPassword: string): Promise<string> {
    return await decryptData(encryptedPassword);
  },

  async encryptPassword(password: string): Promise<string> {
    return await encryptData(password);
  },

  calculatePasswordStrength(password: string): PasswordStrength {
    let score = 0;
    
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    
    if (score <= 2) return 'weak';
    if (score <= 4) return 'medium';
    if (score <= 5) return 'strong';
    return 'very_strong';
  },

  generateSecurePassword(length = 16, includeSymbols = true): string {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const fullCharset = includeSymbols ? charset + symbols : charset;
    
    let password = '';
    for (let i = 0; i < length; i++) {
      password += fullCharset.charAt(Math.floor(Math.random() * fullCharset.length));
    }
    
    return password;
  }
}; 